import { ApiError } from '../identity/types';
import { apiErrorFromResponse } from '../../shared/apiErrorFromResponse';
import { accountingApiBaseUrl, payrollRunApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import { refreshAccessToken } from '../../shared/tokenRefresh';
import type { AccountingGateway } from './AccountingGateway';
import { ExportErrorCode, ExportValidationError } from './exportErrors';
import { HttpPeopleGateway } from '../people/HttpPeopleGateway';
import { CODE_MAPPING_BANNER } from './exportOptions';
import type {
  AccountingExportCapabilities, AccountingMapping, AddGroupMembersInput, ChartAccount, ChartImportResult, CodeBulkApplyInput, CodeBulkPreview, CodeBulkRequest, CodeBulkResult,
  CodeReadiness, CodeReadinessParams, CreateChartAccountInput, CreateEmployeeGroupInput, CreateExportInput, CreateExportResult, CreateMappingInput, DownloadedExport,
  EligiblePayrollParams, EligiblePayrolls, EmployeeCandidate, EmployeeGroup, EmployeeGroupMember, ExportAuditItem, ExportDetail, ExportPreview, ExportSettings, ExportSummary,
  ListChartAccountsParams, ListExportsParams, ListMappingsParams, MappingReadiness, Paged, PreviewParams, ReplacementInput, SaveExportSettingsInput, UpdateChartAccountInput,
  UpdateEmployeeGroupInput, WageGroupGrid, WageGroupParams,
} from './exportTypes';
import type { GlExportResponse, GlMappingLineInput, GlMappingResponse, PayrollRunSummary, ReconciliationResponse } from './types';
import {
  addMembersBody, bulkApplyBody, bulkPreviewBody, chartQuery, codeReadinessQuery, createAccountBody, createExportBody, createGroupBody, createMappingBody, eligiblePayrollsQuery,
  listExportsQuery, mapAudit, mapBulkPreview, mapBulkResult, mapCapabilities, mapChartAccount, mapCodeReadiness, mapCreateExportResult, mapEligiblePayrolls, mapEmployeeGroup,
  mapExportResponse, mapExportSettings, mapExportSummary, mapGroupMember, mapImportResult, mapMapping, mapPaged, mapPreview, mapReadiness, mapWageGroupGrid, mappingsQuery,
  previewQuery, replacementBody, reportFromProblem, reversalBody, saveSettingsBody, updateGroupBody, wageGroupsQuery,
  type RawAuditItem, type RawBulkPreview, type RawChartAccount, type RawCodeReadiness, type RawEligiblePayrolls, type RawEmployeeGroup, type RawExportDetail,
  type RawExportSettings, type RawExportSummary, type RawGroupMember, type RawMapping, type RawPreview, type RawWageGroupGrid,
} from './wireMapping';

const enc = encodeURIComponent;
const EXPORTS = '/api/accounting-exports';
const GROUPS = '/api/accounting-employee-groups';
const MAPPINGS = '/api/accounting-mappings';

function fileNameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      /* fall through to the plain form */
    }
  }
  return /filename="?([^";]+)"?/i.exec(header)?.[1] ?? fallback;
}

export class HttpAccountingGateway implements AccountingGateway {
  private readonly request = createJsonClient(accountingApiBaseUrl);

  // Reads Module 7 directly — see AccountingGateway's own doc comment for why. Filtered client-side to
  // Approved, the same convention every other module's own "only Approved runs are relevant here" read
  // already uses (e.g. Custom Pay Run's own submit-for-review path).
  private readonly payrollRunRequest = createJsonClient(payrollRunApiBaseUrl);

  // Only the group member picker reads the Employees service (the server verifies every employee id again when members are added).
  private readonly people = new HttpPeopleGateway();

  async listApprovedPayrollRuns(signal?: AbortSignal): Promise<PayrollRunSummary[]> {
    const runs = await this.payrollRunRequest<PayrollRunSummary[]>('/api/payroll-runs', undefined, signal);
    return runs.filter((r) => r.status === 'Approved');
  }

  getGlMappings(signal?: AbortSignal): Promise<GlMappingResponse[]> {
    return this.request('/api/gl-mappings', undefined, signal);
  }

  setGlMappings(mappings: GlMappingLineInput[]): Promise<GlMappingResponse[]> {
    return this.request('/api/gl-mappings', { method: 'PUT', body: JSON.stringify({ mappings }) });
  }

  exportToGl(payrollRunId: string): Promise<GlExportResponse> {
    return this.request(`/api/payroll-runs/${payrollRunId}/gl-exports`, { method: 'POST' });
  }

  listGlExports(payrollRunId: string, signal?: AbortSignal): Promise<GlExportResponse[]> {
    return this.request(`/api/payroll-runs/${payrollRunId}/gl-exports`, undefined, signal);
  }

  reconcile(payrollRunId: string): Promise<ReconciliationResponse> {
    return this.request(`/api/payroll-runs/${payrollRunId}/reconciliation`, { method: 'POST' });
  }

  listReconciliations(payrollRunId: string, signal?: AbortSignal): Promise<ReconciliationResponse[]> {
    return this.request(`/api/payroll-runs/${payrollRunId}/reconciliation`, undefined, signal);
  }

  // ---- Payroll Accounting Export. The company is never sent: the server takes it from the session. Responses are mapped by wireMapping.ts. ----

  async getExportCapabilities(signal?: AbortSignal): Promise<AccountingExportCapabilities> {
    return mapCapabilities(await this.request('/api/accounting-export-capabilities', undefined, signal));
  }

  async listEligiblePayrolls(params?: EligiblePayrollParams, signal?: AbortSignal): Promise<EligiblePayrolls> {
    return mapEligiblePayrolls(await this.request<RawEligiblePayrolls>(`${EXPORTS}/eligible-payrolls${eligiblePayrollsQuery(params)}`, undefined, signal));
  }

  async previewExport(params: PreviewParams, signal?: AbortSignal): Promise<ExportPreview> {
    return mapPreview(await this.request<RawPreview>(`${EXPORTS}/preview${previewQuery(params)}`, undefined, signal));
  }

  async createExport(input: CreateExportInput): Promise<CreateExportResult> {
    try {
      return mapCreateExportResult(await this.request(EXPORTS, { method: 'POST', body: JSON.stringify(createExportBody(input)) }));
    } catch (err) {
      // A blocked export is HTTP 422 with the full validation report in `extensions.report`: it is a result to show, not a failure to hide.
      if (err instanceof ApiError && err.status === 422 && err.code === ExportErrorCode.ValidationFailed) {
        const report = reportFromProblem(err.details);
        if (report) throw new ExportValidationError(mapPreview(report), err.message);
      }
      throw err;
    }
  }

  async listExports(params?: ListExportsParams, signal?: AbortSignal): Promise<Paged<ExportSummary>> {
    return mapPaged(await this.request<{ items?: RawExportSummary[] }>(`${EXPORTS}${listExportsQuery(params)}`, undefined, signal), mapExportSummary);
  }

  async getExport(id: string, signal?: AbortSignal): Promise<ExportDetail> {
    return mapExportResponse(await this.request<RawExportDetail>(`${EXPORTS}/${enc(id)}`, undefined, signal));
  }

  async downloadExport(id: string): Promise<DownloadedExport> {
    const url = `${accountingApiBaseUrl}${EXPORTS}/${enc(id)}/download`;
    const doFetch = () => fetch(url, { method: 'GET', credentials: 'include' });
    let response = await doFetch();
    if (response.status === 401 && (await refreshAccessToken())) response = await doFetch();
    if (!response.ok) throw await apiErrorFromResponse(response, `Request to ${EXPORTS}/${id}/download failed with ${response.status}`);
    return { blob: await response.blob(), fileName: fileNameFromDisposition(response.headers.get('Content-Disposition'), `payroll-journal-${id}.csv`) };
  }

  async reverseExport(id: string, reason: string): Promise<ExportDetail> {
    return mapExportResponse(await this.request<RawExportDetail>(`${EXPORTS}/${enc(id)}/reversal`, { method: 'POST', body: JSON.stringify(reversalBody(reason)) }));
  }

  async replaceExport(id: string, input: ReplacementInput): Promise<ExportDetail> {
    return mapExportResponse(await this.request<RawExportDetail>(`${EXPORTS}/${enc(id)}/replacement`, { method: 'POST', body: JSON.stringify(replacementBody(input)) }));
  }

  async syncPayrollStatus(id: string): Promise<ExportDetail> {
    return mapExportResponse(await this.request<RawExportDetail>(`${EXPORTS}/${enc(id)}/sync-payroll-status`, { method: 'POST' }));
  }

  async getExportAudit(id: string, signal?: AbortSignal): Promise<ExportAuditItem[]> {
    return mapAudit(await this.request<{ items?: RawAuditItem[] } | RawAuditItem[]>(`${EXPORTS}/${enc(id)}/audit`, undefined, signal));
  }

  async listChartAccounts(params?: ListChartAccountsParams, signal?: AbortSignal): Promise<Paged<ChartAccount>> {
    return mapPaged(await this.request<{ items?: RawChartAccount[] }>(`/api/chart-of-accounts${chartQuery(params)}`, undefined, signal), mapChartAccount);
  }

  async createChartAccount(input: CreateChartAccountInput): Promise<ChartAccount> {
    return mapChartAccount(await this.request<RawChartAccount>('/api/chart-of-accounts', { method: 'POST', body: JSON.stringify(createAccountBody(input)) }));
  }

  async updateChartAccount(id: string, input: UpdateChartAccountInput): Promise<ChartAccount> {
    const body = { name: input.name.trim(), ...(input.externalReference !== undefined ? { externalReference: input.externalReference?.trim() || null } : {}) };
    return mapChartAccount(await this.request<RawChartAccount>(`/api/chart-of-accounts/${enc(id)}`, { method: 'PUT', body: JSON.stringify(body) }));
  }

  async archiveChartAccount(id: string): Promise<void> {
    await this.request(`/api/chart-of-accounts/${enc(id)}/archive`, { method: 'POST' });
  }

  async restoreChartAccount(id: string): Promise<void> {
    await this.request(`/api/chart-of-accounts/${enc(id)}/restore`, { method: 'POST' });
  }

  async importChartAccounts(csv: string, dryRun: boolean): Promise<ChartImportResult> {
    try {
      return mapImportResult(await this.request('/api/chart-of-accounts/import', { method: 'POST', body: JSON.stringify({ csv, dryRun }) }));
    } catch (err) {
      // A dry run with row errors is a 200; a real import with row errors is 400 CHART_IMPORT_INVALID carrying the same result in `extensions.result`. Either way it is a
      // result to show (nothing was imported), so it is returned, not thrown. A 400 with no result (a wrong header, too large) is still an error with its own message.
      if (err instanceof ApiError && err.status === 400 && err.code === ExportErrorCode.ChartImportInvalid) {
        const extensions = err.details?.extensions as { result?: Partial<ChartImportResult> } | undefined;
        if (extensions?.result) return mapImportResult(extensions.result);
      }
      throw err;
    }
  }

  async listMappings(params?: ListMappingsParams, signal?: AbortSignal): Promise<Paged<AccountingMapping>> {
    return mapPaged(await this.request<{ items?: RawMapping[] }>(`/api/accounting-mappings${mappingsQuery(params)}`, undefined, signal), mapMapping);
  }

  async createMapping(input: CreateMappingInput): Promise<AccountingMapping> {
    return mapMapping(await this.request<RawMapping>('/api/accounting-mappings', { method: 'POST', body: JSON.stringify(createMappingBody(input)) }));
  }

  async endMapping(id: string): Promise<void> {
    await this.request(`/api/accounting-mappings/${enc(id)}/end`, { method: 'POST' });
  }

  async getMappingReadiness(payrollRunId: string, signal?: AbortSignal): Promise<MappingReadiness> {
    return mapReadiness(await this.request(`/api/accounting-mappings/readiness?payrollRunId=${enc(payrollRunId)}`, undefined, signal));
  }

  // ---- Export options, wage groups and code mappings. The company is never sent. ----

  async getExportSettings(signal?: AbortSignal): Promise<ExportSettings> {
    return mapExportSettings(await this.request<RawExportSettings>('/api/accounting-export-settings', undefined, signal));
  }

  async saveExportSettings(input: SaveExportSettingsInput): Promise<ExportSettings> {
    return mapExportSettings(await this.request<RawExportSettings>('/api/accounting-export-settings', { method: 'PUT', body: JSON.stringify(saveSettingsBody(input)) }));
  }

  async listEmployeeGroups(signal?: AbortSignal): Promise<EmployeeGroup[]> {
    const raw = await this.request<{ items?: RawEmployeeGroup[] } | RawEmployeeGroup[]>(GROUPS, undefined, signal);
    return (Array.isArray(raw) ? raw : (raw.items ?? [])).map(mapEmployeeGroup);
  }

  async createEmployeeGroup(input: CreateEmployeeGroupInput): Promise<EmployeeGroup> {
    return mapEmployeeGroup(await this.request<RawEmployeeGroup>(GROUPS, { method: 'POST', body: JSON.stringify(createGroupBody(input)) }));
  }

  async updateEmployeeGroup(id: string, input: UpdateEmployeeGroupInput): Promise<EmployeeGroup> {
    return mapEmployeeGroup(await this.request<RawEmployeeGroup>(`${GROUPS}/${enc(id)}`, { method: 'PUT', body: JSON.stringify(updateGroupBody(input)) }));
  }

  async setEmployeeGroupActive(id: string, active: boolean): Promise<EmployeeGroup> {
    return mapEmployeeGroup(await this.request<RawEmployeeGroup>(`${GROUPS}/${enc(id)}/${active ? 'reactivate' : 'deactivate'}`, { method: 'POST' }));
  }

  async listEmployeeGroupMembers(groupId: string, signal?: AbortSignal): Promise<EmployeeGroupMember[]> {
    const raw = await this.request<{ items?: RawGroupMember[] } | RawGroupMember[]>(`${GROUPS}/${enc(groupId)}/members`, undefined, signal);
    return (Array.isArray(raw) ? raw : (raw.items ?? [])).map(mapGroupMember);
  }

  async addEmployeeGroupMembers(groupId: string, input: AddGroupMembersInput): Promise<void> {
    await this.request(`${GROUPS}/${enc(groupId)}/members`, { method: 'POST', body: JSON.stringify(addMembersBody(input)) });
  }

  async endEmployeeGroupMembership(membershipId: string, effectiveTo: string): Promise<void> {
    await this.request(`${GROUPS}/memberships/${enc(membershipId)}/end`, { method: 'POST', body: JSON.stringify({ effectiveTo }) });
  }

  async listEmployeeCandidates(search?: string, signal?: AbortSignal): Promise<EmployeeCandidate[]> {
    const page = await this.people.listEmployees({ ...(search?.trim() ? { search: search.trim() } : {}), pageSize: 100 }, signal);
    return page.items.map((e) => ({ id: e.id, employeeNumber: e.employeeNumber, name: `${e.firstName} ${e.lastName}`.trim() }));
  }

  async getWageGroups(params: WageGroupParams, signal?: AbortSignal): Promise<WageGroupGrid> {
    return mapWageGroupGrid(await this.request<RawWageGroupGrid>(`${MAPPINGS}/wage-groups${wageGroupsQuery(params)}`, undefined, signal), params.dimension);
  }

  async getCodeReadiness(params?: CodeReadinessParams, signal?: AbortSignal): Promise<CodeReadiness> {
    return mapCodeReadiness(await this.request<RawCodeReadiness>(`${MAPPINGS}/code-readiness${codeReadinessQuery(params)}`, undefined, signal), CODE_MAPPING_BANNER);
  }

  async previewCodeBulk(input: CodeBulkRequest): Promise<CodeBulkPreview> {
    return mapBulkPreview(await this.request<RawBulkPreview>(`${MAPPINGS}/code-bulk/preview`, { method: 'POST', body: JSON.stringify(bulkPreviewBody(input)) }));
  }

  async applyCodeBulk(input: CodeBulkApplyInput): Promise<CodeBulkResult> {
    return mapBulkResult(await this.request(`${MAPPINGS}/code-bulk`, { method: 'POST', body: JSON.stringify(bulkApplyBody(input)) }));
  }
}
